import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  FiSearch, FiEdit, FiTrash2, FiPlus,
  FiMapPin, FiCheckCircle, FiXCircle, FiNavigation,
  FiFilter, FiRefreshCw
} from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import BaseUrl from '../../../Url/BaseUrl';
import {
  setSearchTerm as setSearchTermAction,
  setFilters as setFiltersAction,
  setBusStops as setBusStopsAction,
  setHasSearched as setHasSearchedAction,
  setInitialLoad as setInitialLoadAction,
  resetBusStopsSearch,
} from '../../../redux/Slices/busStopsSlice';

const BusStandsManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { searchTerm, filters, busStops, hasSearched, initialLoad } =
    useSelector((state) => state.busStops);

  const [allBusStands, setAllBusStands] = useState([]);
  const [states, setStates] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [showBusStandCodeSuggestions, setShowBusStandCodeSuggestions] = useState(false);
  const [showBusStandNameSuggestions, setShowBusStandNameSuggestions] = useState(false);
  const [filteredCodeSuggestions, setFilteredCodeSuggestions] = useState([]);
  const [filteredNameSuggestions, setFilteredNameSuggestions] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const sortBusStandsByName = (stands) =>
    [...stands].sort((a, b) =>
      (a.busStandName || '').localeCompare(b.busStandName || '', undefined, {
        sensitivity: 'base',
      })
    );

  const fetchAllBusStands = async () => {
    setLoadingMasterData(true);
    try {
      const response = await axios.get(`${BaseUrl.baseurl}BusStands`, {
        headers: { accept: 'text/plain' },
      });
      setAllBusStands(response.data);
    } catch (error) {
      console.error('Error fetching bus stands master data:', error);
      showNotification('error', t('failedToLoadBusStandsData', 'Failed to load bus stands data'));
    } finally {
      setLoadingMasterData(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${BaseUrl.baseurl}States`, {
        headers: { accept: 'text/plain' },
      });
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
      showNotification('error', t('failedToLoadStatesData', 'Failed to load states data'));
    }
  };

  const fetchZones = async (stateCode) => {
    try {
      const response = await axios.get(`${BaseUrl.baseurl}Zones`, {
        params: stateCode ? { stateCode } : {},
        headers: { accept: 'text/plain' },
      });
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
      showNotification('error', t('failedToLoadZonesData', 'Failed to load zones data'));
    }
  };

  const searchBusStands = async () => {
    setLoading(true);
    try {
      const searchParams = {
        busStandCode: filters.busStandCode || undefined,
        stateCode: filters.stateCode || undefined,
        busStandName: searchTerm || undefined,
      };

      Object.keys(searchParams).forEach(
        (key) => searchParams[key] === undefined && delete searchParams[key]
      );

      if (Object.keys(searchParams).length === 0) {
        dispatch(setBusStopsAction([]));
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${BaseUrl.baseurl}BusStands/BusStandSearch`,
        searchParams,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json',
          },
        }
      );

      const sortedData = sortBusStandsByName(response.data);
      dispatch(setBusStopsAction(sortedData));
      dispatch(setHasSearchedAction(true));
      if (sortedData.length === 0) {
        showNotification('info', t('noBusStandsFound', 'No bus stands found with the given criteria'));
      }
    } catch (error) {
      console.error('Error searching bus stands:', error);
      showNotification('error', t('failedToSearchBusStands', 'Failed to search bus stands'));
      dispatch(setBusStopsAction([]));
      dispatch(setHasSearchedAction(true));
    } finally {
      setLoading(false);
      dispatch(setInitialLoadAction(false));
    }
  };

  useEffect(() => {
    fetchAllBusStands();
    fetchStates();
  }, []);

  useEffect(() => {
    if (filters.stateCode) {
      fetchZones(filters.stateCode);
    } else {
      setZones([]);
      if (filters.zoneCode) {
        dispatch(setFiltersAction({ ...filters, zoneCode: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.stateCode]);

  useEffect(() => {
    if (filters.busStandCode && allBusStands.length > 0) {
      const filtered = allBusStands
        .filter((stand) =>
          stand.busStandCode.toLowerCase().includes(filters.busStandCode.toLowerCase())
        )
        .slice(0, 10);
      setFilteredCodeSuggestions(filtered);
      setShowBusStandCodeSuggestions(filtered.length > 0);
    } else {
      setFilteredCodeSuggestions([]);
      setShowBusStandCodeSuggestions(false);
    }
  }, [filters.busStandCode, allBusStands]);

  useEffect(() => {
    if (searchTerm && allBusStands.length > 0) {
      const filtered = allBusStands
        .filter((stand) =>
          stand.busStandName.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .slice(0, 10);
      setFilteredNameSuggestions(filtered);
      setShowBusStandNameSuggestions(filtered.length > 0);
    } else {
      setFilteredNameSuggestions([]);
      setShowBusStandNameSuggestions(false);
    }
  }, [searchTerm, allBusStands]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 3000);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchBusStands();
    setShowBusStandCodeSuggestions(false);
    setShowBusStandNameSuggestions(false);
  };

  const handleReset = () => {
    dispatch(resetBusStopsSearch());
    setFilteredCodeSuggestions([]);
    setFilteredNameSuggestions([]);
    setShowBusStandCodeSuggestions(false);
    setShowBusStandNameSuggestions(false);
  };

  const handleBack = () => {
    dispatch(resetBusStopsSearch());
    navigate(-1);
  };

  const handleFilterChange = (field, value) => {
    dispatch(setFiltersAction({ ...filters, [field]: value }));
    if (field === 'busStandCode') {
      setShowBusStandCodeSuggestions(true);
    }
  };

  const handleBusStandCodeSelect = (busStand) => {
    dispatch(
      setFiltersAction({
        ...filters,
        busStandCode: busStand.busStandCode,
        busStandName: busStand.busStandName,
      })
    );
    dispatch(setSearchTermAction(busStand.busStandName));
    setShowBusStandCodeSuggestions(false);
  };

  const handleBusStandNameSelect = (busStand) => {
    dispatch(setSearchTermAction(busStand.busStandName));
    dispatch(
      setFiltersAction({
        ...filters,
        busStandCode: busStand.busStandCode,
        busStandName: busStand.busStandName,
      })
    );
    setShowBusStandNameSuggestions(false);
  };

  const handleViewDetails = (busStandCode) => {
    navigate(`/bus-finder/bus-stands/view/${busStandCode}`);
  };

  // ✅ Pass full busStand object via React Router navigation state[cite: 1]
  const handleEdit = (busStand) => {
    navigate(`/bus-finder/bus-stands/edit/${busStand.busStandCode}`, {
      state: { busStandPreview: busStand },
    });
  };

  const handleDelete = (busStand) => {
    setDeleteConfirm(busStand);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      setDeleting(true);
      try {
        const updatedBusStand = { ...deleteConfirm, status: false };

        const response = await fetch(
          `${BaseUrl.baseurl}BusStands/${deleteConfirm.busStandCode}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              accept: 'text/plain',
            },
            body: JSON.stringify(updatedBusStand),
          }
        );

        if (response.ok) {
          dispatch(
            setBusStopsAction(
              sortBusStandsByName(
                busStops.map((stand) =>
                  stand.busStandCode === deleteConfirm.busStandCode
                    ? { ...stand, status: false }
                    : stand
                )
              )
            )
          );
          setAllBusStands((prev) =>
            prev.map((stand) =>
              stand.busStandCode === deleteConfirm.busStandCode
                ? { ...stand, status: false }
                : stand
            )
          );
          showNotification('success', t('deactivatedSuccess', 'Bus stand deactivated successfully'));
        } else {
          const errorText = await response.text();
          throw new Error(
            `${t('deactivateFailed', 'Failed to deactivate bus stand:')} ${errorText}`
          );
        }
      } catch (error) {
        console.error('Error deactivating bus stand:', error);
        showNotification(
          'error',
          `${t('deactivateError', 'Error deactivating bus stand:')} ${error.message}`
        );
      } finally {
        setDeleting(false);
        setDeleteConfirm(null);
      }
    }
  };

  const toggleStatus = async (busStandCode) => {
    try {
      const busStand = busStops.find((stand) => stand.busStandCode === busStandCode);
      const updatedStatus = !busStand.status;
      const updatedBusStand = { ...busStand, status: updatedStatus };

      const response = await fetch(`${BaseUrl.baseurl}BusStands/${busStandCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'text/plain',
        },
        body: JSON.stringify(updatedBusStand),
      });

      if (response.ok) {
        dispatch(
          setBusStopsAction(
            sortBusStandsByName(
              busStops.map((stand) =>
                stand.busStandCode === busStandCode
                  ? { ...stand, status: updatedStatus }
                  : stand
              )
            )
          )
        );
        setAllBusStands((prev) =>
          prev.map((stand) =>
            stand.busStandCode === busStandCode
              ? { ...stand, status: updatedStatus }
              : stand
          )
        );
        showNotification(
          'success',
          t('statusUpdated', 'Bus stand {{status}} successfully!', {
            status: updatedStatus ? t('activated') : t('deactivated'),
          })
        );
      } else {
        const errorText = await response.text();
        throw new Error(
          `${t('httpError', 'HTTP error! status:')} ${response.status}, ${t(
            'details',
            'details:'
          )} ${errorText}`
        );
      }
    } catch (error) {
      console.error('Error updating bus stand status:', error);
      showNotification(
        'error',
        t('updateStatusError', 'Error updating bus stand status. Please try again.')
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.suggestion-container')) {
        setShowBusStandCodeSuggestions(false);
        setShowBusStandNameSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const openInMaps = (lat, lng) => {
    if (!lat || !lng) return;
    const url = `https://www.google.com/maps?q=${lat},${lng}&ll=${lat},${lng}&z=15`;
    window.open(url, '_blank');
  };

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="btn-back" onClick={handleBack}>← Back</button>
      </div>

      {notification.message && (
        <div className={`alert alert-${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: '', message: '' })}>×</button>
        </div>
      )}

      <div className="search-form-container">
        <div className="search-form-header">
          <FiFilter />
          <h3>{t('searchBusStands', 'Search Bus Stands')}</h3>
        </div>
        <form onSubmit={handleSearch} className="search-form">
          <div className="form-grid">
            <div className="form-group suggestion-container">
              <label>{t('busStandCodeLabel', 'Bus Stand Code')}</label>
              <div className="input-with-suggestions">
                <input
                  type="text"
                  placeholder={t('enterBusStandCode', 'Enter bus stand code')}
                  value={filters.busStandCode}
                  onChange={(e) => handleFilterChange('busStandCode', e.target.value)}
                  onFocus={() => setShowBusStandCodeSuggestions(true)}
                />
                {loadingMasterData && (
                  <div className="loading-indicator">
                    <ClipLoader size={16} color="#00BCD4" />
                  </div>
                )}
                {showBusStandCodeSuggestions && filteredCodeSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {filteredCodeSuggestions.map((stand) => (
                      <div
                        key={stand.busStandCode}
                        className="suggestion-item"
                        onClick={() => handleBusStandCodeSelect(stand)}
                      >
                        <div className="suggestion-code">{stand.busStandCode}</div>
                        <div className="suggestion-name">{stand.busStandName}</div>
                        <div className="suggestion-city">
                          {stand.cityVillage?.cityVillageCode ||
                            stand.cityVillageCode ||
                            t('na', 'N/A')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group suggestion-container">
              <label>{t('busStandNameLabel', 'Bus Stand Name')}</label>
              <div className="input-with-suggestions">
                <input
                  type="text"
                  placeholder={t('searchByName', 'Search by name')}
                  value={searchTerm}
                  onChange={(e) => dispatch(setSearchTermAction(e.target.value))}
                  onFocus={() => setShowBusStandNameSuggestions(true)}
                />
                {loadingMasterData && (
                  <div className="loading-indicator">
                    <ClipLoader size={16} color="#00BCD4" />
                  </div>
                )}
                {showBusStandNameSuggestions && filteredNameSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {filteredNameSuggestions.map((stand) => (
                      <div
                        key={stand.busStandCode}
                        className="suggestion-item"
                        onClick={() => handleBusStandNameSelect(stand)}
                      >
                        <div className="suggestion-name">{stand.busStandName}</div>
                        <div className="suggestion-code">{stand.busStandCode}</div>
                        <div className="suggestion-city">
                          {stand.cityVillage?.cityVillageCode ||
                            stand.cityVillageCode ||
                            t('na', 'N/A')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>{t('state', 'State')}</label>
              <select
                value={filters.stateCode}
                onChange={(e) => handleFilterChange('stateCode', e.target.value)}
              >
                <option value="">{t('selectState', 'Select State')}</option>
                {states.map((state) => (
                  <option key={state.stateCode} value={state.stateCode}>
                    {state.stateName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('zone', 'Zone')}</label>
              <select
                value={filters.zoneCode}
                onChange={(e) => handleFilterChange('zoneCode', e.target.value)}
                disabled={!filters.stateCode}
              >
                <option value="">{t('selectZone', 'Select Zone')}</option>
                {zones.map((zone) => (
                  <option key={zone.zoneCode} value={zone.zoneCode}>
                    {zone.zoneName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              <FiRefreshCw /> {t('reset', 'Reset')}
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <ClipLoader size={20} color="#fff" />
              ) : (
                <>
                  <FiSearch /> {t('searchBusStandsButton', 'Search Bus Stands')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {busStops.length > 0 && (
        <>
          <div className="results-header">
            <h3>
              {t('searchResults', 'Search Results')} ({busStops.length}{' '}
              {t('busStandsFound', 'bus stands found')})
            </h3>
            <button
              className="btn-primary"
              onClick={() => navigate('/bus-finder/bus-stands/register')}
            >
              <FiPlus /> {t('registerNewBusStandButton', 'Register New Bus Stand')}
            </button>
          </div>
          <div className="management-table-container">
            <table className="management-table">
              <thead>
                <tr>
                  <th>{t('standCode', 'Stand Code')}</th>
                  <th>{t('standName', 'Stand Name')}</th>
                  <th>{t('cityVillage', 'City/Village')}</th>
                  <th>{t('coordinates', 'Coordinates')}</th>
                  <th>{t('elevation', 'Elevation')}</th>
                  <th>{t('contact', 'Contact')}</th>
                  <th>{t('status', 'Status')}</th>
                  <th>{t('actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {busStops.map((stand) => (
                  <tr key={stand.busStandCode}>
                    <td>
                      <div className="stand-code">
                        <span className="code-badge">{stand.busStandCode}</span>
                      </div>
                    </td>
                    <td>
                      <div className="stand-info">
                        <strong>{stand.busStandName}</strong>
                        {stand.facilities && stand.facilities.length > 0 && (
                          <div className="stand-facilities">
                            {stand.facilities.slice(0, 2).map((facility, index) => (
                              <span key={index} className="facility-tag">
                                {facility}
                              </span>
                            ))}
                            {stand.facilities.length > 2 && (
                              <span className="more-facilities">
                                +{stand.facilities.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="city-info">
                        <span className="city-name">
                          {stand.cityVillage?.cityVillageName || t('na', 'N/A')}
                        </span>
                        {(stand.cityVillage?.cityVillageCode || stand.cityVillageCode) && (
                          <span className="city-code">
                            {stand.cityVillage?.cityVillageCode || stand.cityVillageCode}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="coordinates-info">
                        <div className="coords">
                          {stand.latitude}, {stand.longitude}
                        </div>
                        <button
                          className="btn-map"
                          onClick={() =>
                            openInMaps(stand.latitude, stand.longitude)
                          }
                          title={t('openInGoogleMaps', 'Open in Google Maps')}
                          disabled={!stand.latitude || !stand.longitude}
                        >
                          <FiNavigation size="0.9em" />
                        </button>
                      </div>
                    </td>
                    <td>{stand.elevation || t('na', 'N/A')}</td>
                    <td>{stand.busStandContactNumber || t('na', 'N/A')}</td>
                    <td>
                      <button
                        className={`status-toggle ${stand.status ? 'active' : 'inactive'}`}
                        onClick={() => toggleStatus(stand.busStandCode)}
                        title={
                          stand.status
                            ? t('clickToDeactivate', 'Click to deactivate')
                            : t('clickToActivate', 'Click to activate')
                        }
                      >
                        {stand.status ? (
                          <>
                            <FiCheckCircle /> {t('active', 'Active')}
                          </>
                        ) : (
                          <>
                            <FiXCircle /> {t('inactive', 'Inactive')}
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(stand)}
                          title={t('edit', 'Edit')}
                        >
                          <FiEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(stand)}
                          title={t('deactivateBusStand', 'Deactivate Bus Stand')}
                          disabled={!stand.status}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!initialLoad && busStops.length === 0 && (
        <div className="no-results">
          <div className="no-results-content">
            <FiMapPin size="3em" />
            <h3>{t('noBusStandsFoundTitle', 'No Bus Stands Found')}</h3>
            <p>{t('enterSearchCriteria', 'Enter search criteria to find bus stands')}</p>
            <button
              className="btn-secondary"
              onClick={() => navigate('/bus-finder/bus-stands/register')}
            >
              <FiPlus /> {t('addNewBusStand', 'Add New Bus Stand')}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <ClipLoader size={50} color="#00BCD4" />
          <p>{t('searchingBusStands', 'Searching bus stands...')}</p>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{t('confirmDeactivation', 'Confirm Deactivation')}</h3>
            <p>
              {t(
                'confirmDeactivationMessage',
                'Are you sure you want to deactivate this bus stand? This will set the status to inactive.'
              )}
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <ClipLoader size={16} color="#fff" />
                ) : (
                  t('deactivateBusStand', 'Deactivate Bus Stand')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .management-container {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
        }
        .management-header { margin-bottom: 2rem; }
        .management-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .management-header p { color: #666; font-size: 1rem; }
        .search-form-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        .search-form-header {
          display: flex; align-items: center; gap: 0.5rem;
          margin-bottom: 1.5rem; color: #333;
        }
        .search-form-header h3 { margin: 0; font-size: 1.2rem; }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem; margin-bottom: 1.5rem;
        }
        .form-group {
          display: flex; flex-direction: column; gap: 0.5rem; position: relative;
        }
        .form-group label { font-weight: 600; font-size: 0.9rem; color: #495057; }
        .input-with-suggestions { position: relative; }
        .form-group input, .form-group select {
          padding: 0.75rem;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
          width: 100%; box-sizing: border-box;
        }
        .form-group input:focus, .form-group select:focus {
          outline: none; border-color: #00BCD4;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }
        .loading-indicator {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
        }
        .suggestions-dropdown {
          position: absolute; top: 100%; left: 0; right: 0;
          background: white; border: 1px solid #dee2e6; border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          max-height: 300px; overflow-y: auto; z-index: 1000; margin-top: 2px;
        }
        .suggestion-item {
          padding: 0.75rem 1rem; cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s;
          display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem;
          align-items: center;
        }
        .suggestion-item:hover { background-color: #f8f9fa; }
        .suggestion-item:last-child { border-bottom: none; }
        .suggestion-code {
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.85rem; color: #1976d2; font-weight: 500;
          background: #e3f2fd; padding: 0.2rem 0.5rem; border-radius: 4px;
          text-align: center;
        }
        .suggestion-name {
          font-weight: 500; color: #333; font-size: 0.9rem;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .suggestion-city { font-size: 0.8rem; color: #666; text-align: right; }
        .form-group select:disabled {
          background-color: #f8f9fa; color: #6c757d; cursor: not-allowed;
        }
        .form-actions {
          display: flex; justify-content: flex-end; gap: 1rem;
          padding-top: 1rem; border-top: 1px solid #eee;
        }
        .results-header {
          display: flex; justify-content: space-between; align-items: center;
          margin: 2rem 0 1rem 0; padding: 0 0.5rem;
        }
        .results-header h3 { margin: 0; color: #333; font-size: 1.2rem; }
        .management-table-container {
          background: white; border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1); overflow: hidden;
        }
        .management-table { width: 100%; border-collapse: collapse; }
        .management-table th {
          background: #f8f9fa; padding: 1rem; text-align: left;
          font-weight: 600; color: #495057;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.9rem; text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .management-table td {
          padding: 1rem; border-bottom: 1px solid #eee;
          color: #212529; vertical-align: middle;
        }
        .stand-code { display: flex; align-items: center; }
        .code-badge {
          background: #e3f2fd; color: #1976d2;
          padding: 0.25rem 0.75rem; border-radius: 20px;
          font-size: 0.85rem; font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
        }
        .stand-info { display: flex; flex-direction: column; gap: 0.5rem; }
        .stand-info strong { font-size: 1rem; color: #333; }
        .stand-facilities { display: flex; gap: 0.25rem; flex-wrap: wrap; }
        .facility-tag {
          background: #e8f5e9; color: #2e7d32;
          padding: 0.1rem 0.5rem; border-radius: 4px; font-size: 0.75rem;
        }
        .more-facilities {
          background: #f5f5f5; color: #666;
          padding: 0.1rem 0.5rem; border-radius: 4px; font-size: 0.75rem;
        }
        .city-info { display: flex; flex-direction: column; gap: 0.25rem; }
        .city-name { font-weight: 500; color: #333; }
        .city-code {
          font-size: 0.8rem; color: #666;
          font-family: 'Monaco', 'Courier New', monospace;
        }
        .coordinates-info { display: flex; align-items: center; gap: 0.5rem; }
        .coords {
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.85rem; color: #666;
        }
        .btn-map {
          background: #f8f9fa; border: 1px solid #dee2e6;
          border-radius: 4px; width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #00BCD4; transition: all 0.2s;
        }
        .btn-map:hover:not(:disabled) {
          background: #00BCD4; color: white; border-color: #00BCD4;
        }
        .btn-map:disabled { opacity: 0.5; cursor: not-allowed; }
        .status-toggle {
          padding: 0.4rem 0.8rem; border: none; border-radius: 20px;
          font-size: 0.85rem; font-weight: 500; cursor: pointer;
          display: inline-flex; align-items: center; gap: 0.4rem;
          transition: all 0.2s;
        }
        .status-toggle.active { background: #e8f5e9; color: #2e7d32; }
        .status-toggle.inactive { background: #ffebee; color: #c62828; }
        .status-toggle:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .action-buttons { display: flex; gap: 0.5rem; }
        .btn-icon {
          width: 36px; height: 36px; border: none; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s;
        }
        .btn-edit { background: #fff3e0; color: #f57c00; }
        .btn-delete { background: #ffebee; color: #c62828; }
        .btn-icon:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .no-results {
          display: flex; justify-content: center; align-items: center;
          min-height: 400px;
        }
        .no-results-content { text-align: center; color: #666; }
        .no-results-content h3 { margin: 1rem 0 0.5rem 0; color: #333; }
        .loading-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          display: flex; flex-direction: column;
          justify-content: center; align-items: center; z-index: 1000;
        }
        .loading-overlay p { margin-top: 1rem; color: #666; }
        .alert {
          padding: 1rem 1.5rem; border-radius: 8px;
          margin-bottom: 1.5rem; display: flex;
          justify-content: space-between; align-items: center;
          animation: slideIn 0.3s ease-out;
        }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .alert-error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
        .alert-info { background: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb; }
        .alert button {
          background: none; border: none; font-size: 1.2rem;
          cursor: pointer; color: inherit;
        }
        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black; border: none; padding: 0.75rem 1.5rem;
          border-radius: 8px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 0.5rem;
          transition: all 0.2s;
        }
        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
        }
        .btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-secondary {
          background: #f8f9fa; color: #495057;
          border: 1px solid #dee2e6; padding: 0.75rem 1.5rem;
          border-radius: 8px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 0.5rem;
          transition: all 0.2s;
        }
        .btn-secondary:hover { background: #e9ecef; }
        .btn-danger {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white; border: none; padding: 0.75rem 1.5rem;
          border-radius: 8px; font-weight: 600; cursor: pointer;
          transition: all 0.2s;
        }
        .btn-danger:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);
        }
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex; justify-content: center; align-items: center;
          z-index: 1000;
        }
        .modal {
          background: white; border-radius: 12px; padding: 2rem;
          width: 90%; max-width: 500px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        .modal h3 { margin: 0 0 1rem 0; color: #333; }
        .modal p { color: #666; margin-bottom: 2rem; }
        .modal-actions {
          display: flex; justify-content: flex-end; gap: 1rem;
        }
        @media (max-width: 1024px) {
          .management-container { padding: 1rem; }
          .management-table-container { overflow-x: auto; }
          .management-table { min-width: 1200px; }
        }
        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr; }
          .form-actions { flex-direction: column; }
          .results-header {
            flex-direction: column; gap: 1rem; align-items: stretch;
          }
          .suggestion-item { grid-template-columns: 1fr; gap: 0.25rem; }
          .suggestion-code, .suggestion-name, .suggestion-city {
            text-align: left;
          }
          .action-buttons { flex-direction: column; gap: 0.25rem; }
          .btn-icon { width: 32px; height: 32px; }
        }
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BusStandsManagement;